import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow, isToday, isYesterday, parseISO, format } from "date-fns";
import {
  Activity, UserPlus, UserCheck, Heart, HelpCircle, MessageCircle,
  Inbox, MessageSquare, BookOpen, CheckCheck, Bell,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loading, EmptyState } from "@/components/common/Loading";
import { AuthorAvatar, profileHref } from "@/components/social/AuthorLink";
import { NotificationText } from "@/components/notifications/NotificationText";

const ICONS = {
  connection_request: { Icon: UserPlus, tint: "bg-blue-50 text-blue-600" },
  connection_accepted: { Icon: UserCheck, tint: "bg-emerald-50 text-emerald-600" },
  follow: { Icon: Heart, tint: "bg-rose-50 text-rose-600" },
  question: { Icon: HelpCircle, tint: "bg-amber-50 text-amber-600" },
  question_answered: { Icon: HelpCircle, tint: "bg-emerald-50 text-emerald-600" },
  chatter_reaction: { Icon: Heart, tint: "bg-rose-50 text-rose-600" },
  chatter_comment: { Icon: MessageCircle, tint: "bg-teal-50 text-primary" },
  resource_comment: { Icon: BookOpen, tint: "bg-teal-50 text-primary" },
  inquiry: { Icon: Inbox, tint: "bg-violet-50 text-violet-600" },
  inquiry_response: { Icon: Inbox, tint: "bg-emerald-50 text-emerald-600" },
  message: { Icon: MessageSquare, tint: "bg-blue-50 text-blue-600" },
};

function bucket(iso) {
  try {
    const d = parseISO(iso);
    if (isToday(d)) return "Today";
    if (isYesterday(d)) return "Yesterday";
    return format(d, "MMMM d, yyyy");
  } catch {
    return "Earlier";
  }
}

export default function ActivityFeed() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["activity-notifications"],
    queryFn: async () => (await api.get("/notifications", { params: { limit: 100 } })).data,
  });

  const markAll = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["activity-notifications"] });
    qc.invalidateQueries({ queryKey: ["notif-unread"] });
  };

  const open = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      qc.invalidateQueries({ queryKey: ["activity-notifications"] });
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
    }
    if (n.link) navigate(n.link);
  };

  if (isLoading) return <Loading />;

  const items = data?.notifications || [];
  const unread = data?.unread || 0;

  // group by date bucket, preserving recency order
  const groups = [];
  const seen = {};
  items.forEach((n) => {
    const b = bucket(n.created_at);
    if (!(b in seen)) { seen[b] = groups.length; groups.push({ label: b, items: [] }); }
    groups[seen[b]].items.push(n);
  });

  return (
    <div data-testid="activity-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Activity Feed</h1>
          <p className="mt-1 text-muted-foreground">Everything happening around your profiles and network.</p>
        </div>
        {unread > 0 && (
          <Button variant="secondary" size="sm" onClick={markAll} data-testid="activity-mark-all-read">
            <CheckCheck className="mr-2 h-4 w-4" /> Mark all read
          </Button>
        )}
      </div>

      <div className="mt-6">
        {items.length === 0 ? (
          <EmptyState icon={Activity} title="No activity yet" description="When members connect, follow, ask questions or react to your updates, it'll show up here." testId="activity-empty" />
        ) : (
          <div className="space-y-7">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="mb-2 text-xs font-[640] uppercase tracking-wide text-muted-foreground">{g.label}</div>
                <Card className="border-0 p-0">
                  <ul className="divide-y divide-border/70">
                    {g.items.map((n) => {
                      const meta = ICONS[n.type] || { Icon: Bell, tint: "bg-secondary text-muted-foreground" };
                      const actor = n.actor;
                      const actorHref = profileHref(actor);
                      const actorName = actor && actor.display_name;
                      return (
                        <li
                          key={n.id}
                          onClick={() => open(n)}
                          className={`flex cursor-pointer items-start gap-3 px-5 py-3.5 transition-colors hover:bg-secondary/60 ${n.read ? "" : "bg-teal-50/40"}`}
                          data-testid={`activity-item-${n.id}`}
                        >
                          <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.tint}`}>
                            <meta.Icon className="h-[18px] w-[18px]" />
                          </span>
                          {actor && (
                            <AuthorAvatar card={actor}>
                              <Avatar className="h-9 w-9 shrink-0">
                                {actor.headshot_url && <AvatarImage src={fileUrl(actor.headshot_url)} alt="" />}
                                <AvatarFallback className="bg-teal-50 text-xs text-teal-700">
                                  {(actor.display_name || "M")[0]}
                                </AvatarFallback>
                              </Avatar>
                            </AuthorAvatar>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-foreground">
                              {Array.isArray(n.tokens) && n.tokens.length > 0 ? (
                                <NotificationText n={n} />
                              ) : actorHref && actorName && n.text.startsWith(actorName) ? (
                                <>
                                  <Link to={actorHref} onClick={(e) => e.stopPropagation()} className="font-[600] hover:underline" data-testid={`activity-actor-${n.id}`}>{actorName}</Link>
                                  {n.text.slice(actorName.length)}
                                </>
                              ) : n.text}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}
                            </p>
                          </div>
                          {!n.read && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" data-testid={`activity-unread-${n.id}`} />}
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
